import React, { useState, useEffect } from 'react';
import { CheckCircle } from 'lucide-react';
import MarkForAnalysis from './BidFactorySteps/MarkForAnalysis';
import PDFStagingModal from './BidFactorySteps/PDFStagingModal';
import AgentWorkflowUI from './BidFactorySteps/AgentWorkflowUI';
import ResponseWriting from './BidFactorySteps/ResponseWriting';
import './BidFactoryWizard.css';

const STEPS = [
  { id: 1, name: 'Configure Analysis', description: 'Set parameters & review tender' },
  { id: 2, name: 'Stage PDFs', description: 'Upload additional documents' },
  { id: 3, name: '5-Phase Analysis', description: 'Running sourcing pipeline' },
  { id: 4, name: 'Write Response', description: 'Build your bid response' }
];

const BidFactoryWizard = ({ tender, onComplete, onCancel }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [wizardState, setWizardState] = useState({
    tender: tender,
    uploadedPdfs: [],
    existingPdfs: [],
    analysisInProgress: false,
    analysisComplete: false,
    // Analyst configuration from Step 1
    analystConfig: {
      exchangeRate: 1.36,
      shippingOrigin: 'Shenzhen, China',
      shippingDestination: 'Vancouver, Canada',
      dutyRate: 0.05,
      defectAllowance: 0.03,
      warrantyAllowance: 0.02,
      marginBuffer: 0.15,
      quantities: '',
      analystNotes: ''
    },
    // 5-Phase analysis results
    analysisResults: {
      phase0: null, // Assumptions & Scoring
      phase1: null, // Technical Extraction
      phase2: null, // Canada Pricing Map
      phase3: null, // OEM/China Sourcing
      phase4: null, // Competitor Banding
      phase5: null, // Bid Pricing
      // Legacy fields for backward compatibility
      benchmark: null,
      similarContracts: null,
      buyerAnalysis: null,
      recommendation: null,
      processingStage: null
    },
    response: {
      executive_summary: '',
      technical_approach: '',
      project_team: '',
      timeline: '',
      budget: '',
      additional_info: ''
    }
  });

  // Load any saved progress
  useEffect(() => {
    const savedProgress = localStorage.getItem(`bid_factory_${tender?.tender_id || tender?.id}`);
    if (savedProgress) {
      try {
        const parsed = JSON.parse(savedProgress);
        setWizardState(prev => ({ ...prev, ...parsed }));
        // If analysis was complete, jump to step 4
        if (parsed.analysisComplete) {
          setCurrentStep(4);
        }
      } catch (e) {
        console.error('Error loading saved progress:', e);
      }
    }
  }, [tender]);

  // Auto-save progress
  useEffect(() => {
    if (tender) {
      // Don't save File objects to localStorage - they can't be serialized
      // Only save metadata about PDFs (name, id, url, status)
      const pdfMetadata = (wizardState.uploadedPdfs || []).map(pdf => ({
        id: pdf.id,
        name: pdf.name,
        size: pdf.size,
        url: pdf.url,
        status: pdf.status
        // Note: 'file' property (File object) is intentionally excluded
      }));

      const toSave = {
        uploadedPdfs: pdfMetadata,
        analystConfig: wizardState.analystConfig,
        analysisComplete: wizardState.analysisComplete,
        analysisResults: wizardState.analysisResults,
        response: wizardState.response
      };
      localStorage.setItem(`bid_factory_${tender.tender_id || tender.id}`, JSON.stringify(toSave));
    }
  }, [wizardState, tender]);

  const goToStep = (step) => {
    if (step >= 1 && step <= 4) {
      // Allow going back, but only forward if previous step is complete
      if (step < currentStep || canProceedToStep(step)) {
        setCurrentStep(step);
      }
    }
  };

  const canProceedToStep = (step) => {
    switch (step) {
      case 2: return currentStep >= 1;
      case 3: return currentStep >= 2;
      case 4: return wizardState.analysisComplete;
      default: return true;
    }
  };

  const handleStartAnalysis = (config) => {
    // Save config from Step 1
    setWizardState(prev => ({
      ...prev,
      analystConfig: config
    }));
    setCurrentStep(2);
  };

  const handleAnalysisComplete = (results) => {
    // Clear cached report so new analysis results generate fresh report
    const tenderId = tender?.tender_id || tender?.id;
    if (tenderId) {
      localStorage.removeItem(`bid_response_${tenderId}`);
      localStorage.removeItem(`bid_response_${tenderId}_timestamp`);
      console.log('[BidFactoryWizard] Cleared cached report for new analysis results');
    }

    // Add a regeneration timestamp to force ResponseWriting to generate fresh
    const resultsWithTimestamp = {
      ...results,
      _regenerateAt: Date.now()
    };

    setWizardState(prev => ({
      ...prev,
      analysisInProgress: false,
      analysisComplete: true,
      analysisResults: resultsWithTimestamp
    }));
    setCurrentStep(4);
  };

  const handleRerunAnalysis = () => {
    // Clear cached report so new analysis generates fresh report
    const tenderId = tender?.tender_id || tender?.id;
    if (tenderId) {
      localStorage.removeItem(`bid_response_${tenderId}`);
      localStorage.removeItem(`bid_response_${tenderId}_timestamp`);
      console.log('[BidFactoryWizard] Cleared cached report for rerun');
    }

    // Clear previous results and go back to AI analysis step
    setWizardState(prev => ({
      ...prev,
      analysisInProgress: true,
      analysisComplete: false,
      analysisResults: {
        phase0: null,
        phase1: null,
        phase2: null,
        phase3: null,
        phase4: null,
        phase5: null,
        benchmark: null,
        similarContracts: null,
        buyerAnalysis: null,
        recommendation: null,
        processingStage: null
      }
    }));
    setCurrentStep(3);
  };

  const handleResponseChange = (field, value) => {
    setWizardState(prev => ({
      ...prev,
      response: { ...prev.response, [field]: value }
    }));
  };

  const handlePdfUpload = (files) => {
    setWizardState(prev => ({
      ...prev,
      uploadedPdfs: [...prev.uploadedPdfs, ...files]
    }));
  };

  const handleSkipPdf = () => {
    setCurrentStep(3);
    setWizardState(prev => ({
      ...prev,
      analysisInProgress: true
    }));
  };

  const handleContinueWithPdfs = () => {
    setCurrentStep(3);
    setWizardState(prev => ({
      ...prev,
      analysisInProgress: true
    }));
  };

  const renderSidebar = () => (
    <div className="wizard-sidebar">
      <div className="sidebar-header">
        <h2>Bid Factory</h2>
        <p className="sidebar-subtitle">
          5-Phase Sourcing Analysis
          <strong>{tender?.title || 'Selected Tender'}</strong>
        </p>
      </div>
      <div className="wizard-steps">
        {STEPS.map((step, index) => {
          const isActive = currentStep === step.id;
          const isComplete = currentStep > step.id || (step.id === 4 && wizardState.analysisComplete);
          const isClickable = step.id <= currentStep || canProceedToStep(step.id);

          return (
            <div
              key={step.id}
              className={`wizard-step ${isActive ? 'active' : ''} ${isComplete ? 'complete' : ''} ${isClickable ? 'clickable' : ''}`}
              onClick={() => isClickable && goToStep(step.id)}
            >
              <div className="step-indicator">
                {isComplete ? (
                  <CheckCircle size={20} />
                ) : (
                  <div className="step-number">{step.id}</div>
                )}
              </div>
              <div className="step-info">
                <span className="step-name">{step.name}</span>
                <span className="step-description">{step.description}</span>
              </div>
              {index < STEPS.length - 1 && (
                <div className={`step-connector ${currentStep > step.id ? 'complete' : ''}`} />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <MarkForAnalysis
            tender={wizardState.tender}
            onStartAnalysis={handleStartAnalysis}
            onCancel={onCancel}
          />
        );
      case 2:
        return (
          <PDFStagingModal
            tender={wizardState.tender}
            existingPdfs={wizardState.existingPdfs}
            uploadedPdfs={wizardState.uploadedPdfs}
            onUpload={handlePdfUpload}
            onSkip={handleSkipPdf}
            onContinue={handleContinueWithPdfs}
            onBack={() => setCurrentStep(1)}
          />
        );
      case 3:
        return (
          <AgentWorkflowUI
            tender={wizardState.tender}
            uploadedPdfs={wizardState.uploadedPdfs}
            analystConfig={wizardState.analystConfig}
            onComplete={handleAnalysisComplete}
            onCancel={() => {
              setWizardState(prev => ({ ...prev, analysisInProgress: false }));
              setCurrentStep(2);
            }}
          />
        );
      case 4:
        return (
          <ResponseWriting
            tender={wizardState.tender}
            analysisResults={wizardState.analysisResults}
            analystConfig={wizardState.analystConfig}
            response={wizardState.response}
            onResponseChange={handleResponseChange}
            onComplete={onComplete}
            onBack={() => setCurrentStep(1)}
            onRerunAnalysis={handleRerunAnalysis}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="bid-factory-wizard">
      {renderSidebar()}
      <div className="wizard-main">
        <div className="wizard-content">
          {renderCurrentStep()}
        </div>
      </div>
    </div>
  );
};

export default BidFactoryWizard;
